import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaDownload, FaFileExport, FaChartBar, FaTable } from 'react-icons/fa';
import HeaderActionButton from './ui/HeaderActionButton';
import apiClient from '../utils/apiClient';
import ExportModal from './ExportModal';

/**
 * Export Manager avec design harmonisé
 */
const ExportManagerStyled = ({ nodes = [], relations = [], tests = [], defaultOpen = false, onModalClose }) => {
  const { t } = useTranslation();
  const [isExportModalOpen, setExportModalOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);

  const handleExport = async (type, data, destination) => {
    //console.log('🚀 Début de l\'export:', { type, dataCount: data.items?.length || 0, destination });
    
    try {
      setLoading(true);
      
      const response = await apiClient.post('/api/export', {
        type, 
        data, 
        destination
      });
      
      //console.log('📡 Réponse du serveur:', response.status, response.statusText);
      //console.log('✅ Résultat de l\'export:', response.data);
      
      if (response.data.success) {
        const content = response.data.fullContent || response.data.markdownPreview || '';
        const fileExtension = response.data.fileExtension || 'md';
        const contentType = response.data.contentType || 'text/markdown';
        const filename = `export-${data.type || type}-${new Date().toISOString().slice(0, 10)}.${fileExtension}`;
        
        // Solution antivirus : ouvrir dans une nouvelle fenêtre avec boutons pour TOUS les formats
        const newWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
        
        if (response.data.fileFormat === 'html') {
          // Pour HTML : afficher le rendu ET donner accès au code source
          newWindow.document.write(`
            <html>
              <head>
                <title>${filename}</title>
                <style>
                  body { font-family: Arial, sans-serif; margin: 0; }
                  .toolbar { background: #f5f5f5; padding: 10px; border-bottom: 1px solid #ddd; position: sticky; top: 0; z-index: 100; }
                  .toolbar button { padding: 8px 16px; margin: 0 5px; background: #007cba; color: white; border: none; border-radius: 4px; cursor: pointer; }
                  .toolbar button:hover { background: #005a87; }
                  .html-content { padding: 20px; }
                  .source-view { display: none; padding: 20px; }
                  .source-view textarea { width: 100%; height: 60vh; font-family: monospace; }
                </style>
              </head>
              <body>
                <div class="toolbar">
                  <button onclick="toggleView()">🔄 ${t('exportManager.buttons.toggleView')}</button>
                  <button onclick="copyHTML()">📋 ${t('exportManager.buttons.copyHTML')}</button>
                  <button onclick="downloadHTML()">💾 ${t('exportManager.buttons.download')}</button>
                  <button onclick="window.print()">🖨️ ${t('exportManager.buttons.print')}</button>
                </div>
                <div id="rendered" class="html-content">
                  ${content}
                </div>
                <div id="source" class="source-view">
                  <h3>${t('exportManager.sourceCode.title')} :</h3>
                  <textarea readonly>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
                </div>
                <script>
                  let showingSource = false;
                  function toggleView() {
                    const rendered = document.getElementById('rendered');
                    const source = document.getElementById('source');
                    if (showingSource) {
                      rendered.style.display = 'block';
                      source.style.display = 'none';
                      showingSource = false;
                    } else {
                      rendered.style.display = 'none';
                      source.style.display = 'block';
                      showingSource = true;
                    }
                  }
                  function copyHTML() {
                    const htmlContent = \`${content.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
                    navigator.clipboard.writeText(htmlContent).then(() => {
                      alert('${t('exportManager.messages.htmlCopied')}');
                    });
                  }
                  function downloadHTML() {
                    const htmlContent = \`${content.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
                    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '${filename}';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }
                </script>
              </body>
            </html>
          `);
          newWindow.document.close();
          toast.success(`✅ ${t('exportManager.messages.htmlExportSuccess')}`);
        } else {
          // Pour les autres formats : afficher dans une fenêtre avec textarea
          newWindow.document.write(`
            <html>
              <head>
                <title>${filename}</title>
                <style>
                  body { font-family: Arial, sans-serif; margin: 20px; }
                  .header { background: #f5f5f5; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
                  .content { width: 100%; height: 70vh; font-family: monospace; }
                  .buttons { margin: 10px 0; }
                  button { padding: 10px 20px; margin: 5px; background: #007cba; color: white; border: none; border-radius: 5px; cursor: pointer; }
                  button:hover { background: #005a87; }
                </style>
              </head>
              <body>
                <div class="header">
                  <h2>📄 ${filename}</h2>
                  <p>${t('exportManager.fileInfo.format')}: ${response.data.fileFormat?.toUpperCase()} | ${t('exportManager.fileInfo.size')}: ${content.length} ${t('exportManager.fileInfo.characters')}</p>
                  <div class="buttons">
                    <button onclick="document.getElementById('content').select(); document.execCommand('copy'); alert('${t('exportManager.messages.contentCopied')}')">📋 ${t('exportManager.buttons.copyAll')}</button>
                    <button onclick="downloadFile()">💾 ${t('exportManager.buttons.forceDownload')}</button>
                  </div>
                </div>
                <textarea id="content" class="content" readonly>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
                <script>
                  function downloadFile() {
                    const blob = new Blob([document.getElementById('content').value], { type: '${contentType};charset=utf-8' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '${filename}';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }
                </script>
              </body>
            </html>
          `);
          newWindow.document.close();
          toast.success(`✅ ${t('exportManager.messages.exportSuccess', { destination, format: response.data.fileFormat?.toUpperCase() })}`);
        }
      } else {
        toast.error(`❌ ${t('exportManager.messages.exportFailed')}: ${response.data.message}`);
      }
    } catch (error) {
      console.error('❌ Erreur d\'export:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`❌ ${t('exportManager.messages.exportError')}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const totalItems = nodes.length + relations.length + tests.length;

  return (
    <>
      <HeaderActionButton 
        onClick={() => setExportModalOpen(true)}
        gradient="blue"
        loading={loading}
        className="export-trigger export-manager-trigger"
      >
        <div className="button-header">
          <div className="button-icon"><FaDownload /></div>
          <div className="button-text-content">
            <h3 className="button-title">{t('exportManagerStyled.title', { defaultValue: 'Export' })}</h3>
            <p className="button-subtitle">{t('exportManagerStyled.subtitle', { defaultValue: 'Sauvegardez votre travail' })}</p>
          </div>
          <span className="button-badge">{totalItems} {t('exportManagerStyled.stats.items', { defaultValue: 'éléments' })}</span>
        </div>
        
        <div className="button-features">
          <div className="button-feature">
            <FaFileExport />
            <span>JSON</span>
          </div>
          <div className="button-feature">
            <FaChartBar />
            <span>Markdown</span>
          </div>
          <div className="button-feature">
            <FaTable />
            <span>CSV</span>
          </div>
        </div>
        
        <div className="button-cta export-manager-cta">
          <span className="button-cta-text">
            <span className="cta-icon" style={{ marginRight: '8px', fontSize: '1.1em' }}>📥💾</span>
            {t('exportManagerStyled.buttonText', { defaultValue: 'Exporter les données' })}
          </span>
        </div>
      </HeaderActionButton>

      {isExportModalOpen && (
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => { setExportModalOpen(false); if (onModalClose) onModalClose(); }}
          onExport={handleExport}
          nodes={nodes}
          relations={relations}
          tests={tests}
        />
      )}
    </>
  );
};

export default ExportManagerStyled;